import { BookingRequestsStatus } from "../../../components/BookingRequest/typings/booking-requests.types";
import {
  getBookingAction,
  isCancelAllowed,
  isVisitSlotActive
} from "../../../components/FleetManagement/EventDetailDialog/functions/getBookingAction.function";
import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
export type VisitorBookingAdminContext = {
  /** Request admin assigned to the row location (used to bypass REQUESTED-only UI gates). */
  canManageRequestedBookings?: boolean;
};

export type VisitorBookingRegistrationActionVisibility = {
  showCheckIn: boolean;
  showCheckOut: boolean;
  canReject: boolean;
  canCancel: boolean;
  /** Shown in the reject slot while checked in and not yet checked out. */
  showCheckedInStatus: boolean;
  /** Shown in the reject slot while the parent booking is still REQUESTED. */
  showAwaitingApprovalStatus: boolean;
};

/** Visitor is on-site (checked in, check-out still allowed). */
export function isVisitorBookingCheckedInActive(visit: BookingScheduleInterface): boolean {
  return visit.checkedIn === true && visit.checkedOut !== true;
}

/** Parent booking still awaiting approval — reception actions and delete/selection stay off. */
export function isVisitorBookingAwaitingApproval(visit: BookingScheduleInterface): boolean {
  return visit.status === BookingRequestsStatus.REQUESTED;
}

export function canDeleteVisitorBookingRow(
  visit: BookingScheduleInterface,
  ctx?: VisitorBookingAdminContext
): boolean {
  const awaitingApproval = isVisitorBookingAwaitingApproval(visit);
  if (awaitingApproval && !ctx?.canManageRequestedBookings) {
    return false;
  }
  return !isVisitorBookingCheckedInActive(visit);
}

/**
 * Visitor admin registration buttons: check-in from fleet rules (`getBookingAction`);
 * check-out while checked in until end-of-end-day; reject while visit slot active, approved, and not
 * checked in; check-out until end-of-end-day (`isCancelAllowed`). When checked in or `REQUESTED`
 * (unless request admin for the location), reject and delete/selection are unavailable; REQUESTED
 * and checked-in rows show a status chip in the actions column instead. Cancel is not offered
 * admin table (use reject); `canCancel` is always false. Optional `visit.visitRegistrationActions`
 * from BE can deny actions; check-in still requires fleet start-day rules (`getBookingAction`).
 */
export function getVisitorBookingRegistrationActionVisibility(
  visit: BookingScheduleInterface,
  ctx?: VisitorBookingAdminContext
): VisitorBookingRegistrationActionVisibility {
  const startIso = visit.start ?? visit.startDate;
  const endIso = visit.end ?? visit.endDate;
  const { timezone } = visit;

  const hasRange = Boolean(startIso && endIso);
  const primaryAction = hasRange
    ? getBookingAction(
        startIso as string,
        endIso as string,
        timezone,
        visit.checkedIn,
        visit.checkedOut === true
      )
    : null;

  const cancelUntilEndOfDay = endIso ? isCancelAllowed(endIso, timezone) : false;
  const rejectWhileSlotActive = endIso ? isVisitSlotActive(endIso, timezone) : false;

  const checkedInActive = isVisitorBookingCheckedInActive(visit);
  const awaitingApproval = isVisitorBookingAwaitingApproval(visit);
  const requestAdminBypass = awaitingApproval && (ctx?.canManageRequestedBookings ?? false);
  const blockRequestedActions = awaitingApproval && !requestAdminBypass;

  const scheduleCanReject = !checkedInActive && rejectWhileSlotActive;

  const fromSchedule: VisitorBookingRegistrationActionVisibility = {
    showCheckIn: primaryAction === "check-in",
    showCheckOut: checkedInActive && cancelUntilEndOfDay,
    canReject: !blockRequestedActions && scheduleCanReject,
    canCancel: false,
    showCheckedInStatus: checkedInActive,
    showAwaitingApprovalStatus: awaitingApproval && !requestAdminBypass
  };

  const flags = visit.visitRegistrationActions;
  if (!flags) {
    return fromSchedule;
  }

  const canRejectFromFlags = flags.rejectAllowed ?? scheduleCanReject;
  let canReject: boolean;
  if (checkedInActive) {
    canReject = false;
  } else if (requestAdminBypass) {
    canReject = scheduleCanReject;
  } else if (awaitingApproval) {
    canReject = false;
  } else {
    canReject = canRejectFromFlags;
  }

  return {
    showCheckIn: fromSchedule.showCheckIn && (flags.checkInAllowed ?? true),
    showCheckOut: flags.checkOutAllowed ?? fromSchedule.showCheckOut,
    canReject,
    canCancel: false,
    showCheckedInStatus: checkedInActive,
    showAwaitingApprovalStatus: awaitingApproval && !requestAdminBypass
  };
}
